import { useEffect } from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import { Map as MapIcon, Satellite } from 'lucide-react'
import { cn } from '@/lib/utils'

type MapTypeId = 'roadmap' | 'satellite'

/**
 * Keeps React state in sync with the map's mapTypeId. Render inside `<Map>`.
 */
export function MapTypeSync({
  mapTypeId,
  onMapTypeIdChange,
}: {
  mapTypeId: MapTypeId
  onMapTypeIdChange: (id: MapTypeId) => void
}) {
  const map = useMap()

  useEffect(() => {
    if (!map) return
    map.setMapTypeId(mapTypeId)
  }, [map, mapTypeId])

  useEffect(() => {
    if (!map) return
    const sync = () => {
      const id = map.getMapTypeId()
      onMapTypeIdChange(id === 'satellite' || id === 'hybrid' ? 'satellite' : 'roadmap')
    }
    sync()
    const listener = map.addListener('maptypeid_changed', sync)
    return () => listener.remove()
  }, [map, onMapTypeIdChange])

  return null
}

/**
 * Compact vertical Map / Satellite buttons — place top-right under the search bar.
 */
export function MapTypeToggleButtons({
  mapTypeId,
  onChange,
  className,
}: {
  mapTypeId: MapTypeId
  onChange: (id: MapTypeId) => void
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border border-border bg-white shadow-lg',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Map view"
        aria-pressed={mapTypeId === 'roadmap'}
        title="Map"
        onClick={() => onChange('roadmap')}
        className={cn(
          'flex h-10 w-10 items-center justify-center transition',
          mapTypeId === 'roadmap' ? 'bg-navy text-white' : 'text-navy hover:bg-surface',
        )}
      >
        <MapIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Satellite view"
        aria-pressed={mapTypeId === 'satellite'}
        title="Satellite"
        onClick={() => onChange('satellite')}
        className={cn(
          'flex h-10 w-10 items-center justify-center border-t border-border transition',
          mapTypeId === 'satellite' ? 'bg-navy text-white' : 'text-navy hover:bg-surface',
        )}
      >
        <Satellite className="h-5 w-5" />
      </button>
    </div>
  )
}
